import { Datagrid, TextField, DateField, NumberField, useGetList, BooleanField, Title, useNotify } from 'react-admin';
import { Box, Button, Typography, Skeleton, Stack } from '@mui/material';
import { useState, useEffect } from 'react';

interface Review {
    id: string;
    user: {
        id: string;
        name: string;
        isActive: boolean;
    };
    lastUpdatedDate: string;
    content: {
        value: number;
        isTextExist: boolean;
        isPhotosExist: boolean;
    };
    itemInfo: {
        brand: string;
        model: string;
    };
    meta?: {
        cursor: string | null;
    };
}

const LoadingSkeleton = () => (
    <Box sx={{ width: '100%' }}>
        <Stack spacing={1}>
            {[...Array(5)].map((_, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 2, p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Skeleton variant="text" width={200} />
                    <Skeleton variant="text" width={150} />
                    <Skeleton variant="text" width={180} />
                    <Skeleton variant="text" width={80} />
                    <Skeleton variant="text" width={60} />
                    <Skeleton variant="text" width={60} />
                    <Skeleton variant="text" width={100} />
                    <Skeleton variant="text" width={100} />
                </Box>
            ))}
        </Stack>
    </Box>
);

export const ReviewsList = () => {
    const [cursor, setCursor] = useState<string | undefined>(undefined);
    const [allReviews, setAllReviews] = useState<Review[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const notify = useNotify();

    const {
        data,
        total,
        isLoading,
        refetch
    } = useGetList<Review>(
        'reviews',
        {
            pagination: { page: 1, perPage: 30 },
            sort: { field: 'lastUpdatedDate', order: 'DESC' },
            meta: { cursor }
        }
    );

    useEffect(() => {
        if (data && data.length > 0) {
            const lastReview = data[data.length - 1];
            const newCursor = lastReview.meta?.cursor;
            setHasMore(newCursor !== null);
        } else {
            setHasMore(false);
        }
    }, [data]);

    const handleLoadMore = async () => {
        if (data && data.length > 0) {
            const lastReview = data[data.length - 1];
            const newCursor = lastReview.meta?.cursor;
            if (newCursor !== null) {
                setCursor(newCursor);
                setAllReviews(prev => [...prev, ...data]);
                await refetch();
            } else {
                setHasMore(false);
            }
        }
    };

    const handleCopyId = (id: string) => {
        navigator.clipboard.writeText(id);
        notify('ID скопирован в буфер обмена', { type: 'success' });
    };

    if (isLoading && allReviews.length === 0) {
        return (
            <Box>
                <Title title="Отзывы на модерацию" />
                <Box sx={{ mt: 2 }}>
                    <LoadingSkeleton />
                </Box>
            </Box>
        );
    }

    const currentReviews = data || [];
    const displayReviews = [...allReviews, ...currentReviews];

    if (displayReviews.length === 0 && !isLoading) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Title title="Отзывы на модерацию" />
                <Typography variant="h6" align="center" sx={{ mt: 4 }}>
                    Список отзывов на модерацию пуст
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Title title="Отзывы на модерацию" />
            <Box sx={{ mt: 2 }}>
                <Datagrid 
                    data={displayReviews}
                    rowClick="show"
                    bulkActionButtons={false}
                    sx={{
                        '& .MuiTableCell-root': {
                            py: 2
                        }
                    }}
                >
                    <TextField 
                        source="id" 
                        label="ID" 
                        onClick={(e) => {
                            e.stopPropagation();
                            handleCopyId(e.currentTarget.textContent || '');
                        }}
                        sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    />
                    <TextField source="user.name" label="Пользователь" />
                    <DateField source="lastUpdatedDate" label="Дата обновления" showTime />
                    <NumberField source="content.value" label="Оценка" />
                    <BooleanField source="content.isTextExist" label="Текст" />
                    <BooleanField source="content.isPhotosExist" label="Фото" />
                    <TextField source="itemInfo.brand" label="Бренд" />
                    <TextField source="itemInfo.model" label="Модель" />
                </Datagrid>
            </Box>
            
            <Box sx={{ mt: 3, textAlign: "center" }}>
                {hasMore && (
                    <Button
                        disabled={isLoading}
                        onClick={handleLoadMore}
                        variant="contained"
                        sx={{ 
                            minWidth: '200px',
                            py: 1.5
                        }}
                    >
                        {isLoading ? 'Загрузка...' : 'Загрузить еще'}
                    </Button>
                )}
            </Box>
        </Box>
    );
}; 